import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { Button, Flex, Form, InputNumber, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdvancedTable, {
  AddButton,
  AdvancedTableRef,
  EditableColumnType,
  getActionColumns,
} from "../components/AdvancedTable";
import { useKeyValueData } from "../contexts/KeyValueDataProvider";
import {
  usePropStrengthData,
} from "../contexts/PropStrengthDataProvider";
import {
  createDefaultPropStrengthRows,
  PropStrength,
} from "../shared/types/PropStrength";
import { dataCall } from "../tsrpc";

const { Title } = Typography;

export default function PagePropStrength({
  backText = "返回道具列表",
  onBack,
  closeButton,
}: {
  backText?: string;
  onBack?: () => void;
  closeButton?: boolean;
} = {}) {
  return (
    <InnerComponent backText={backText} onBack={onBack} closeButton={closeButton} />
  );
}

function InnerComponent({
  backText,
  onBack,
  closeButton,
}: {
  backText: string;
  onBack?: () => void;
  closeButton?: boolean;
}) {
  const navigate = useNavigate();
  const tableRef = useRef<AdvancedTableRef<PropStrength>>(null);
  const context = usePropStrengthData();
  const [editingData, setEditingData] = useState<PropStrength | undefined>();
  const { data: rarities, load: loadRarities } =
    useKeyValueData("proprarities");
  const { data: types, load: loadTypes } = useKeyValueData("proptypes");
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!rarities || rarities.length === 0) loadRarities();
    if (!types || types.length === 0) loadTypes();
    getVersion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection: "propstrengths" },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  const fillDefaultStrengths = async () => {
    const rows = createDefaultPropStrengthRows(rarities, types);
    const additions: Omit<PropStrength, "_id">[] = [];
    for (const row of rows) {
      const existing = context.data.find((item) => Number(item.rarity) === Number(row.rarity));
      if (existing) {
        await context.update({ _id: existing._id }, { $set: row });
      } else {
        additions.push(row);
      }
    }
    if (additions.length) await context.add(additions);
    await context.load({}, true);
    await getVersion();
  };

  const TableTitle = () => {
    return (
      <Flex justify="space-between" align="center">
        <Flex gap={10}>
          <Title level={5}>道具强度管理</Title>
          <p>{`[数据版本：${version}]`}</p>
        </Flex>
        <Flex gap={10}>
          <Button
            onClick={fillDefaultStrengths}
          >
            填充默认配置
          </Button>
          <AddButton<PropStrength>
            title="新增"
            add={() => context.add}
            createValue={() => {
              const unusedRarity =
                rarities.find((rarity) =>
                  !context.data.some((item) => Number(item.rarity) === Number(rarity.value)),
                ) ?? rarities[0];
              const rarityValue = Number(unusedRarity?.value ?? 1);
              const res: Omit<PropStrength, "_id"> = { rarity: rarityValue };
              types.forEach((type) => {
                res[Number(type.value)] = 0;
              });
              return res;
            }}
          >
            <Form.Item
              name="rarity"
              label="品质"
              rules={[{ required: true, message: "请输入品质！" }]}
            >
              <InputNumber min={1} max={100} precision={0} />
            </Form.Item>
            {types.map((type) => (
              <Form.Item key={type.value} name={type.value} label={type.label}>
                <InputNumber min={0} max={10000} precision={2} />
              </Form.Item>
            ))}
          </AddButton>
          {closeButton ? (
            <Tooltip title="关闭">
              <Button
                icon={<CloseOutlined />}
                onClick={onBack ? onBack : () => navigate("/control/props")}
              />
            </Tooltip>
          ) : (
            <Button
              icon={<LeftOutlined />}
              onClick={onBack ? onBack : () => navigate("/control/props")}
            >
              {backText}
            </Button>
          )}
        </Flex>
      </Flex>
    );
  };

  const columns: EditableColumnType<PropStrength>[] = [
    {
      title: "品质",
      dataIndex: "rarity",
      width: 160,
      render: (rarity: number) =>
        rarities.find((item) => Number(item.value) === Number(rarity))?.label ?? rarity,
      editor: <InputNumber min={1} max={100} precision={0} />,
    },
    ...types.map((type) => ({
      title: type.label || String(type.value),
      dataIndex: type.value,
      editor: <InputNumber min={0} max={10000} precision={2} />,
    })),
    getActionColumns({
      editingData,
      setEditingData,
      display: (d) => `品质${d?.rarity}强度配置`,
      editSubmit: () => tableRef.current?.editSubmit,
      $delete: () => tableRef.current?.delete,
    }),
  ];

  return (
    <AdvancedTable<PropStrength>
      ref={tableRef}
      editingData={editingData}
      context={context}
      title={() => <TableTitle />}
      columns={columns}
    />
  );
}
